import { useState, useEffect, useContext } from 'react';
import { Grid, Button, Typography, makeStyles, Select, MenuItem, InputLabel, FormControl } from '@material-ui/core';
import { getUsersLeagues, updateUserLeagues } from '../../../../api_helper/api';
import SnackbarAlertContext from '../../../../context/SnackbarAlertContext';
import AddFantasyLeagueDialog from './AddFantasyLeagueDialog';
import { useLocation, useHistory } from 'react-router-dom';
import { UserProfileContext } from '../../../../UserProfileContext';
import getLogo from '../../../../utils/getLogo';
import { trackEvent } from '../../../../utils/eventTracking';
import EditDataSpaceFormContext from '../../context/EditDataSpaceFormContext';
import { getUserMessage } from '../../../../utils/handleError';
import {
	FANTRAX_API,
	getAttachedFantasyDataset,
	getFantraxLeagueDisplayName,
	isEditFantasyLeagueOptionDisabled,
} from '../../../../utils/fantasyLeagueHelpers';

const useStyles = makeStyles((theme) => ({
	singleSelect: {
		width: '100%',
		maxWidth: '25rem',
		marginBottom: '0.5rem',
	},
}));

// The SelectFantasyLeague is a dropdown input that allows the user to select a connected fantasy league as a datasource.
const SelectFantasyLeague = (props) => {
	let history = useHistory();
	const { search } = useLocation();
	const params = new URLSearchParams(search);
	const classes = useStyles();

	const userProfileContext = useContext(UserProfileContext)[0];
	const snackbarAlertContext = useContext(SnackbarAlertContext);
	const editDataSpaceFormContext = useContext(EditDataSpaceFormContext);

	const [isDialogOpen, setIsDialogOpen] = useState(false);

	// This function displays an Alert to the user based on a given severity.
	// Severities include success, info, and error.
	const displayAlert = (alertType, customAlert) => {
		let alertConfigs = {
			refreshSuccess: {
				msg: 'Leagues refreshed successfully.',
				severity: 'success',
				autoHide: 5000,
			},
			refreshInProgress: {
				msg: 'Working on refreshing leagues...',
				severity: 'info',
				autoHide: null,
			},
			refreshError: {
				msg: 'Error refreshing leagues. Please try again later',
				severity: 'error',
				autoHide: 5000,
			},
		};

		if (customAlert) {
			snackbarAlertContext.setSnackbarAlert({ ...customAlert, isSnackbarOpen: true });
		} else {
			snackbarAlertContext.setSnackbarAlert({ ...alertConfigs[alertType], isSnackbarOpen: true });
		}
	};

	const attachedFantasyDataset = getAttachedFantasyDataset(editDataSpaceFormContext.dataSpace);

	const matchesAttachedLeague = (league, fantasyLeagueDataset) => {
		if (fantasyLeagueDataset.tags.api === FANTRAX_API) {
			return String(league.leagueId) === String(fantasyLeagueDataset.leagueKey);
		}

		return (league.league_key || league.leagueId) === fantasyLeagueDataset.leagueKey;
	};

	// Calls the Azure function GetUsersLeagues and updates the list of fantasy leagues
	const updateLeagues = async () => {
		// Reset the selected fantasy league
		editDataSpaceFormContext.setSelectedFantasyLeague(undefined);

		let fantasyLeagues = (await getUsersLeagues()).leagues;

		const fantasyLeagueDataset = getAttachedFantasyDataset(editDataSpaceFormContext.dataSpace);

		if (fantasyLeagueDataset) {
			const fantasyLeague = fantasyLeagues.find((league) => matchesAttachedLeague(league, fantasyLeagueDataset));
			editDataSpaceFormContext.setSelectedFantasyLeague(fantasyLeague);
		}

		editDataSpaceFormContext.setFantasyLeagues(fantasyLeagues);
	};

	const selectedFantasyLeagueChangeHandler = async (e) => {
		const fantasyLeague = e.target.value;
		if (!fantasyLeague) {
			return;
		}

		const updatedDatasets = editDataSpaceFormContext.dataSpace.datasets.map((datasetInstance) => {
			if (datasetInstance.tags.api === 'Yahoo! Fantasy Sports') {
				datasetInstance.userId = fantasyLeague.userId;
				datasetInstance.leagueName = fantasyLeague.name;
				datasetInstance.leagueKey = fantasyLeague.league_key;
				datasetInstance.gameKey = fantasyLeague.league_key.substring(0, 3);
			}

			if (datasetInstance.tags.api === FANTRAX_API) {
				datasetInstance.userId = fantasyLeague.userId;
				datasetInstance.leagueName = fantasyLeague.name;
				datasetInstance.leagueKey = fantasyLeague.leagueId;
			}

			return datasetInstance;
		});

		const updatedJoinData = editDataSpaceFormContext.dataSpace.joinData.map((datasetInstance) => {
			if (datasetInstance.tags.api === 'Yahoo! Fantasy Sports') {
				datasetInstance.userId = fantasyLeague.userId;
				datasetInstance.leagueName = fantasyLeague.name;
				datasetInstance.leagueKey = fantasyLeague.league_key;
				datasetInstance.gameKey = fantasyLeague.league_key.substring(0, 3);
			}

			if (datasetInstance.tags.api === FANTRAX_API) {
				datasetInstance.userId = fantasyLeague.userId;
				datasetInstance.leagueName = fantasyLeague.name;
				datasetInstance.leagueKey = fantasyLeague.leagueId;
			}

			return datasetInstance;
		});

		editDataSpaceFormContext.setDataSpace({
			...editDataSpaceFormContext.dataSpace,
			datasets: updatedDatasets,
			joinData: updatedJoinData,
		});

		editDataSpaceFormContext.setSelectedFantasyLeague(fantasyLeague);
	};

	const handleClickOpenDialog = () => {
		setIsDialogOpen(true);
	};

	// Updates leagues from fantasy APIs
	const handleClickRefreshLeagues = async () => {
		try {
			editDataSpaceFormContext.setFantasyLeagues(undefined);
			editDataSpaceFormContext.setSelectedFantasyLeague(undefined);

			await updateUserLeagues({ userId: userProfileContext._id });

			// Fetch and update leagues.
			updateLeagues();
			displayAlert('refreshSuccess');
		} catch (e) {
			snackbarAlertContext.setSnackbarAlert({ msg: getUserMessage(e), severity: 'error' });
			editDataSpaceFormContext.setFantasyLeagues([]);
			return;
		}
	};

	// Populate User League dropdown.
	useEffect(() => {
		if (params.get('route') === 'dataSpaceForm') {
			try {
				trackEvent({
					userDetails: { userId: userProfileContext._id, email: userProfileContext.email },
					eventDetails: {
						types: ['KissMetrics', 'Segment', 'Encharge', 'AppInsights'],
						eventName: 'WebApp_User Connects Yahoo! Fantasy Sports Account',
					},
				});
				handleClickRefreshLeagues();
				history.replace('/app');
			} catch (e) {
				displayAlert(null, { msg: 'Failed to fetch fantasy league(s).', severity: 'error', autoHide: 5000 });
			}
		} else {
			const loadLeagues = async () => {
				const hasFantraxDataset = getAttachedFantasyDataset(editDataSpaceFormContext.dataSpace)?.tags.api === FANTRAX_API;

				if (hasFantraxDataset) {
					try {
						await updateUserLeagues({ userId: userProfileContext._id });
					} catch (e) {
						// Fall through to cached leagues so the dropdown still renders.
					}
				}

				await updateLeagues();
			};

			loadLeagues();
		}
	}, []);

	return (
		<Grid item container xs={12} md={6} style={{ marginBottom: '1rem', ...props.style }}>
			<AddFantasyLeagueDialog
				isDialogOpen={isDialogOpen}
				setIsDialogOpen={setIsDialogOpen}
				updateLeagues={updateLeagues}
				callBackRoute={'dataSpaceForm'}
			/>

			<Grid item xs={12}>
				<Typography variant="h6" align="left" marginBottom="0.5rem">
					Select Your Fantasy League
				</Typography>
			</Grid>

			<Grid item container xs={12}>
				<FormControl fullWidth>
					<Select
						displayEmpty
						className={classes.singleSelect}
						onChange={selectedFantasyLeagueChangeHandler}
						value={editDataSpaceFormContext.selectedFantasyLeague || null}
						renderValue={(fantasyLeague) => {
							return !editDataSpaceFormContext.fantasyLeagues ? (
								<span style={{ color: 'grey' }}>Loading...</span>
							) : !fantasyLeague ? (
								<span style={{ color: 'grey' }}>No league selected</span>
							) : (
								<div style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
									<img
										src={getLogo({ api: fantasyLeague.api })}
										style={{ width: '2rem', borderRadius: '0.2rem', marginRight: '0.5rem' }}
									/>
									<span
										style={{
											width: '100%',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
										}}
									>
										{getFantraxLeagueDisplayName(fantasyLeague)}
									</span>
								</div>
							);
						}}
					>
						{editDataSpaceFormContext.fantasyLeagues ? (
							editDataSpaceFormContext.fantasyLeagues.length ? (
								editDataSpaceFormContext.fantasyLeagues.map((fantasyLeague) => (
									<MenuItem
										key={fantasyLeague._id || fantasyLeague.leagueId || fantasyLeague.league_key}
										value={fantasyLeague}
										disabled={isEditFantasyLeagueOptionDisabled({
											fantasyLeague,
											selectedFantasyLeague: editDataSpaceFormContext.selectedFantasyLeague,
											selectedSport: editDataSpaceFormContext.selectedSport,
											attachedFantasyDataset,
										})}
										onClick={() => {
											if (fantasyLeague.api === FANTRAX_API) {
												selectedFantasyLeagueChangeHandler({ target: { value: fantasyLeague } });
											}
										}}
									>
										<div style={{ display: 'flex', alignItems: 'center' }}>
											<img
												src={getLogo({ api: fantasyLeague.api })}
												style={{ width: '2rem', borderRadius: '0.2rem', marginRight: '0.5rem' }}
											/>
											{getFantraxLeagueDisplayName(fantasyLeague)}
										</div>
									</MenuItem>
								))
							) : (
								<MenuItem disabled={true}>No leagues connected</MenuItem>
							)
						) : (
							<MenuItem disabled={true}>Loading...</MenuItem>
						)}
					</Select>
				</FormControl>
			</Grid>

			<Grid item container xs={12} md={12}>
				<Button size="small" variant="contained" color="secondary" margin="normal" onClick={handleClickOpenDialog}>
					Connect New Leagues
				</Button>

				<Button
					margin="normal"
					onClick={async () => {
						// Open info alert to tell user refresh is in progress
						displayAlert('refreshInProgress');
						await handleClickRefreshLeagues();
					}}
				>
					Refresh Leagues
				</Button>
			</Grid>
		</Grid>
	);
};

export default SelectFantasyLeague;
